import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
} from '@angular/core';
import { RecipeDetailService } from '@app/mpp/pages/recipe-detail/services/recipe-detail.service';
import { MppService } from '@app/mpp/service/mpp.service';
import { IonNav, NavController } from '@ionic/angular';
import { PlatformEnum } from '@panamax/app-state';
import { RecipeImageService } from '@app/mpp/shared/services/recipe-image/recipe-image.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-edit-recipe-details-modal',
  templateUrl: './edit-recipe-details-modal.component.html',
  styleUrls: ['./edit-recipe-details-modal.component.scss'],
})
export class EditRecipeDetailsModalComponent implements AfterViewInit {
  @Input() platform: PlatformEnum;
  @Input() imageSrc: string;
  @Input() description: string;
  @Input() groupName: string;
  @Input() recipeName: string;
  @Input() groupNames: string[];
  @Input() step: number;
  @Input() recipeId: string;
  @Output() dismissEvent = new EventEmitter<void>();
  @Output() submitEvent = new EventEmitter<Map<string, string>>();

  tempGroupName = '';
  private imageSubscription: Subscription;

  platformRef = PlatformEnum;
  formData = {
    name: '',
    description: '',
    group: '',
  };
  nameIsEmpty = false;
  nameContainsEmojis = false;
  nameIsDuplicate = false;
  descriptionContainsEmojis = false;
  groupContainsEmojis = false;

  constructor(
    private mppService: MppService,
    private recipeDetailService: RecipeDetailService,
    private cdr: ChangeDetectorRef,
    private recipeImageService: RecipeImageService,
  ) {}

  ngAfterViewInit(): void {
    this.formData.name = this.recipeName;
    this.formData.description = this.description;
    this.formData.group = this.groupName;
  }

  dismiss(): void {
    this.dismissEvent.emit();
  }

  async submit(): Promise<void> {
    if (this.step === 1) {
      const nameValid = await this.isNameValid(this.formData.name);
      const descriptionValid = await this.isDescriptionValid(
        this.formData.description,
      );
      const groupValid = this.isGroupValid(this.formData.group);
      if (nameValid && descriptionValid && groupValid) {
        const updates = new Map<string, string>();
        if (this.formData.name !== this.recipeName) {
          updates.set('name', this.formData.name);
        }
        if (this.formData.description !== this.description) {
          updates.set('description', this.formData.description);
        }
        if (this.formData.group !== this.groupName) {
          updates.set('group', this.formData.group);
        }
        this.submitEvent.emit(updates);
      } else {
        this.cdr.detectChanges();
      }
    } else {
      this.step = 1;
      this.cdr.detectChanges();
    }
  }

  async isNameValid(name: string): Promise<boolean> {
    this.nameIsEmpty = !name || name.trim() === '';
    this.nameContainsEmojis = /\p{Extended_Pictographic}/u.test(name);
    this.nameIsDuplicate = await this.mppService.doesMppNameExists(
      name,
      'recipe',
    );
    if (this.formData.name === this.recipeName) {
      this.nameIsDuplicate = false;
    }

    return (
      !this.nameIsEmpty && !this.nameContainsEmojis && !this.nameIsDuplicate
    );
  }

  isGroupValid(group: string): boolean {
    this.groupContainsEmojis = /\p{Extended_Pictographic}/u.test(group);
    return !this.groupContainsEmojis;
  }

  async isDescriptionValid(description: string): Promise<boolean> {
    this.descriptionContainsEmojis = /\p{Extended_Pictographic}/u.test(
      description,
    );
    return !this.descriptionContainsEmojis;
  }

  onGroupSelection(event: any): void {
    this.formData.group = event.detail.value;
  }

  async onNameChange(value: string): Promise<void> {
    const nameValid = await this.isNameValid(value);
    this.cdr.detectChanges();
  }

  onGroupChange(value: string): void {
    const groupValid = this.isGroupValid(value);
    this.cdr.detectChanges();
  }

  onDescriptionChange(value: string): void {
    const descriptionValid = this.isDescriptionValid(value);
    this.cdr.detectChanges();
  }

  onMoveToGroup(): void {
    this.step = 2;
    this.cdr.detectChanges();
  }

  onAddGroup(): void {
    if (
      this.tempGroupName !== '' &&
      !this.groupNames.includes(this.tempGroupName) &&
      this.isGroupValid(this.tempGroupName)
    ) {
      this.groupNames.push(this.tempGroupName);
      this.formData.group = this.tempGroupName;
      this.tempGroupName = '';
    }
    this.cdr.detectChanges();
  }

  saveChangeFunction(formData: any) {
    this.formData = formData;
  }

  goBackAndSave() {
    if (this.step === 1) {
      this.dismiss();
    } else {
      this.step = 1;
      this.cdr.detectChanges();
    }
  }

  openProfileImageModal(): void {
    this.recipeDetailService.openProfileImageModal();
  }
}
